import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';
import { listTimelineCandidates, getCandidateTimeline, exportCandidateTimeline } from '../../api/documents';
import { saveBlobResponse } from '../../utils/download';
import { pushToast } from '../../components/ui/toastBus';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';
import { SlaTable } from '../../components/candidates/SlaTab';

import {
  IconActivity, IconArrowRight, IconCandidates, IconCheckCircle, IconClock, IconEdit, IconLeads,
  IconReports, IconSkipForward, IconSwap, IconTrash, IconUndo, IconWorkflow, IconXCircle,
} from '../../components/ui/icons';

const humanize = (action) => action.replace(/[._-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const dayKey = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const timeOf = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

function relative(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  const units = [['day', 86400], ['hour', 3600], ['minute', 60]];
  for (const [name, secs] of units) {
    if (diff >= secs) {
      const n = Math.floor(diff / secs);
      return `${n} ${name}${n === 1 ? '' : 's'} ago`;
    }
  }
  return '';
}

// Event domain → filter chip. Decided by the action's prefix.
const DOMAINS = [
  { key: 'all', label: 'All' },
  { key: 'workflow', label: 'Workflow' },
  { key: 'documents', label: 'Documents' },
  { key: 'profile', label: 'Profile' },
  { key: 'requests', label: 'Requests' },
  { key: 'lead', label: 'Lead stage' },
];
const domainOf = (e) => {
  if (e.is_lead_activity || e.action.startsWith('lead.')) return 'lead';
  if (e.action.startsWith('step.')) return 'workflow';
  if (e.action.startsWith('document.')) return 'documents';
  if (e.action.startsWith('bulk_request.')) return 'requests';
  if (e.action.startsWith('candidate.')) return 'profile';
  return 'profile';
};

const TONES = {
  green: { dot: 'bg-green-500', soft: 'bg-green-50 text-green-700 ring-green-200' },
  red: { dot: 'bg-red-500', soft: 'bg-red-50 text-red-700 ring-red-200' },
  amber: { dot: 'bg-amber-500', soft: 'bg-amber-50 text-amber-700 ring-amber-200' },
  blue: { dot: 'bg-blue-500', soft: 'bg-blue-50 text-blue-700 ring-blue-200' },
  gray: { dot: 'bg-gray-400', soft: 'bg-gray-50 text-gray-600 ring-gray-200' },
};

// Picks the icon + tone for an event from its action name.
function styleFor(e) {
  const a = e.action;
  if (a === 'step.completed' || a === 'step.moved_on') return { Icon: IconCheckCircle, tone: 'green' };
  if (a === 'step.failed_policy' || a.endsWith('.rejected')) return { Icon: IconXCircle, tone: 'red' };
  if (a === 'step.reverted' || a === 'candidate.reopened') return { Icon: IconUndo, tone: 'amber' };
  if (a === 'step.skipped') return { Icon: IconSkipForward, tone: 'gray' };
  if (a === 'candidate.category_changed') return { Icon: IconSwap, tone: 'blue' };
  if (a === 'document.verified' || a === 'bulk_request.approved') return { Icon: IconCheckCircle, tone: 'green' };
  if (a.endsWith('.deleted')) return { Icon: IconTrash, tone: 'red' };
  if (a === 'document.unverified') return { Icon: IconXCircle, tone: 'amber' };
  if (a.startsWith('document.')) return { Icon: IconReports, tone: 'blue' };
  if (a.startsWith('lead.')) return { Icon: IconLeads, tone: 'gray' };
  if (a.startsWith('step.')) return { Icon: IconWorkflow, tone: 'blue' };
  if (a === 'candidate.created') return { Icon: IconCandidates, tone: 'green' };
  if (a.endsWith('.updated')) return { Icon: IconEdit, tone: 'blue' };
  return { Icon: IconActivity, tone: 'gray' };
}

const initials = (name) => (name || '?').split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
const show = (v) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

// "changes" is free-form JSON: render {field: {old,new}} / {field: [old,new]} as a diff and
// anything else as plain key/value rows.
function ChangeList({ changes }) {
  if (!changes || typeof changes !== 'object') return null;
  const entries = Array.isArray(changes) ? changes.map((v, i) => [String(i + 1), v]) : Object.entries(changes);
  if (entries.length === 0) return null;
  return (
    <dl className="mt-2 divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-100 bg-gray-50/60 text-xs">
      {entries.map(([key, val]) => {
        const pair =
          val && typeof val === 'object' && !Array.isArray(val) && ('old' in val || 'new' in val || 'from' in val || 'to' in val)
            ? [val.old ?? val.from, val.new ?? val.to]
            : Array.isArray(val) && val.length === 2 && val.every((x) => typeof x !== 'object' || x === null)
              ? val
              : null;
        return (
          <div key={key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5">
            <dt className="w-36 shrink-0 font-medium text-gray-500">{humanize(key)}</dt>
            <dd className="min-w-0 flex-1 text-gray-800">
              {pair ? (
                <span className="inline-flex flex-wrap items-center gap-1.5">
                  <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700 line-through decoration-red-300">{show(pair[0])}</span>
                  <IconArrowRight className="h-3 w-3 text-gray-400" />
                  <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">{show(pair[1])}</span>
                </span>
              ) : (
                <span className="break-words">{show(val)}</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function TimelineEvent({ event, last }) {
  const [open, setOpen] = useState(false);
  const { Icon, tone } = styleFor(event);
  const t = TONES[tone];
  const hasChanges = event.changes && Object.keys(event.changes).length > 0;
  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {!last && <span className="absolute left-[17px] top-9 h-[calc(100%-2.25rem)] w-px bg-gray-200" />}
      <span className={`relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1 ${t.soft}`}>
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1 rounded-xl border border-gray-100 bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">{humanize(event.action)}</span>
              {event.is_lead_activity && <Badge tone="gray">Lead stage</Badge>}
            </div>
            <p className="mt-0.5 text-sm text-gray-600">{event.description}</p>
          </div>
          <div className="shrink-0 text-right" title={new Date(event.created_at).toLocaleString()}>
            <div className="text-xs font-medium text-gray-700">{timeOf(event.created_at)}</div>
            <div className="text-[11px] text-gray-400">{relative(event.created_at)}</div>
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-[10px] font-semibold text-gray-600">
              {initials(event.user)}
            </span>
            {event.user || 'System'}
          </div>
          {hasChanges && (
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-medium text-primary hover:underline">
              {open ? 'Hide details' : 'View details'}
            </button>
          )}
        </div>
        {hasChanges && open && <ChangeList changes={event.changes} />}
      </div>
    </li>
  );
}

function changeSummary(changes) {
  if (!changes || typeof changes !== 'object') return '';
  const entries = Array.isArray(changes) ? changes.map((v, i) => [String(i + 1), v]) : Object.entries(changes);
  return entries
    .map(([key, val]) => {
      if (val && typeof val === 'object' && !Array.isArray(val) && ('old' in val || 'new' in val || 'from' in val || 'to' in val)) {
        return `${humanize(key)}: ${show(val.old ?? val.from)} → ${show(val.new ?? val.to)}`;
      }
      if (Array.isArray(val) && val.length === 2 && val.every((x) => typeof x !== 'object' || x === null)) {
        return `${humanize(key)}: ${show(val[0])} → ${show(val[1])}`;
      }
      return `${humanize(key)}: ${show(val)}`;
    })
    .join('\n');
}

function TimelineTable({ events }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">Date &amp; time</th>
            <th className="px-3 py-2">Event</th>
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2">By</th>
            <th className="px-3 py-2">Details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white align-top">
          {events.map((e, i) => {
            const { Icon, tone } = styleFor(e);
            const detail = changeSummary(e.changes);
            return (
              <tr key={e.id} className="hover:bg-gray-50/70">
                <td className="px-3 py-2 text-xs text-gray-400">{events.length - i}</td>
                <td className="whitespace-nowrap px-3 py-2">
                  <div className="font-medium text-gray-900">{new Date(e.created_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  <div className="text-xs text-gray-400">{timeOf(e.created_at)} · {relative(e.created_at)}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${TONES[tone].soft}`}>
                    <Icon className="h-3.5 w-3.5" />
                    {humanize(e.action)}
                  </span>
                  {e.is_lead_activity && <span className="ml-1.5 text-[11px] text-gray-400">Lead stage</span>}
                </td>
                <td className="px-3 py-2 text-gray-700">{e.description}</td>
                <td className="whitespace-nowrap px-3 py-2 text-gray-600">{e.user || 'System'}</td>
                <td className="whitespace-pre-line px-3 py-2 text-xs text-gray-500">{detail || '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2">
      <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 truncate text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}

async function downloadTimelineExcel(candidate) {
  try {
    saveBlobResponse(await exportCandidateTimeline(candidate.id), `timeline-${candidate.reference_no}.xlsx`);
    return true;
  } catch {
    pushToast({ type: 'error', message: 'Could not export the timeline.' });
    return false;
  }
}

function ExcelButton({ candidate }) {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      variant="secondary"
      loading={busy}
      onClick={async () => {
        setBusy(true);
        await downloadTimelineExcel(candidate);
        setBusy(false);
      }}
    >
      Excel
    </Button>
  );
}

function CandidateTimelineModal({ candidate, onClose }) {
  const [domain, setDomain] = useState('all');
  const [term, setTerm] = useState('');
  const [view, setView] = useState('sla'); // 'table' | 'timeline' | 'sla'
  const [exporting, setExporting] = useState(false);
  const query = useQuery({
    queryKey: ['candidate-timeline', candidate?.id],
    queryFn: () => getCandidateTimeline(candidate.id),
    enabled: !!candidate,
  });
  const events = useMemo(() => query.data || [], [query.data]);

  useEffect(() => {
    setDomain('all');
    setTerm('');
    setView('sla');
  }, [candidate?.id]);

  const counts = useMemo(() => {
    const c = { all: events.length };
    events.forEach((e) => {
      const d = domainOf(e);
      c[d] = (c[d] || 0) + 1;
    });
    return c;
  }, [events]);

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    return events.filter(
      (e) =>
        (domain === 'all' || domainOf(e) === domain) &&
        (!q || `${e.action} ${e.description} ${e.user || ''}`.toLowerCase().includes(q))
    );
  }, [events, domain, term]);

  // Group by day, keeping the newest-first order from the API.
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((e) => {
      const key = dayKey(e.created_at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return [...map.entries()];
  }, [filtered]);

  const handleExport = async () => {
    setExporting(true);
    await downloadTimelineExcel(candidate);
    setExporting(false);
  };

  const newest = events[0];
  const oldest = events[events.length - 1];
  const shortDate = (iso) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

  return (
    <Modal
      open={!!candidate}
      onClose={onClose}
      size="xl"
      title={candidate ? `${candidate.full_name} · ${candidate.reference_no}` : ''}
      footer={
        <>
          <Button variant="secondary" onClick={handleExport} loading={exporting} disabled={events.length === 0}>
            Download Excel
          </Button>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </>
      }
    >
      {candidate && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryTile label="Events" value={events.length} />
          <SummaryTile label="First activity" value={oldest ? shortDate(oldest.created_at) : '—'} />
          <SummaryTile label="Last activity" value={newest ? relative(newest.created_at) : '—'} />
          <SummaryTile label="Current step" value={candidate.current_step?.title || '—'} />
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2">
        {view !== 'sla' && DOMAINS.filter((d) => d.key === 'all' || counts[d.key]).map((d) => (
          <button
            key={d.key}
            type="button"
            onClick={() => setDomain(d.key)}
            className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition-colors ${
              domain === d.key ? 'bg-primary text-white ring-primary' : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'
            }`}
          >
            {d.label} <span className={domain === d.key ? 'text-white/80' : 'text-gray-400'}>{counts[d.key] || 0}</span>
          </button>
        ))}
        <div className="ml-auto inline-flex overflow-hidden rounded-lg ring-1 ring-gray-200">
          {[['sla', 'SLA'], ['table', 'Table'], ['timeline', 'Timeline']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className={`px-3 py-1.5 text-xs font-medium ${view === key ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
        {view !== 'sla' && <input
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search activity…"
          className="w-48 rounded-lg border border-gray-200 px-3 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />}
      </div>

      {view !== 'sla' && query.isLoading && <p className="py-8 text-center text-sm text-gray-500">Loading timeline…</p>}
      {view !== 'sla' && query.isError && <p className="py-8 text-center text-sm text-red-600">Could not load timeline.</p>}
      {view !== 'sla' && query.isSuccess && filtered.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-10 text-gray-400">
          <IconClock className="h-8 w-8" />
          <p className="text-sm">{events.length === 0 ? 'No activity recorded for this candidate.' : 'No activity matches these filters.'}</p>
        </div>
      )}

      {view === 'table' && filtered.length > 0 && <TimelineTable events={filtered} />}

      {view === 'sla' && candidate && <SlaTable candidateId={candidate.id} />}

      <div className={`space-y-8 ${view !== 'timeline' ? 'hidden' : ''}`}>
        {groups.map(([day, items]) => (
          <section key={day}>
            <div className="sticky top-0 z-20 -mx-1 mb-4 bg-white/95 px-1 py-1 backdrop-blur">
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-600">{day}</span>
            </div>
            <ol>
              {items.map((e, i) => (
                <TimelineEvent key={e.id} event={e} last={i === items.length - 1} />
              ))}
            </ol>
          </section>
        ))}
      </div>
    </Modal>
  );
}

export default function CandidateTimeline() {
  const filters = useCandidateFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const candidatesQuery = useQuery({
    queryKey: ['timeline-candidates', filters.params, search],
    queryFn: () => listTimelineCandidates({ ...filters.params, search: search || undefined }),
  });
  const candidates = useMemo(() => candidatesQuery.data || [], [candidatesQuery.data]);

  const columns = [
    { key: 'reference_no', label: 'Reference No' },
    { key: 'full_name', label: 'Full Name' },
    { key: 'workflow_category', label: 'Workflow' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'contact_no', label: 'Contact' },
    {
      key: 'current_step',
      label: 'Current Step',
      render: (row) => row.current_step?.title || <span className="text-gray-400">—</span>,
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setViewing(row)}>
            View timeline
          </Button>
          <ExcelButton candidate={row} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Timeline"
        description="Find candidates by vehicle type, location, license type, workflow, step or name, and view every activity recorded for them."
      />

      <CandidateFilterBar {...filters.barProps}>
        <Input placeholder="Search name, reference, contact…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </CandidateFilterBar>

      {(
        <>
          <SearchSummary search={search} total={candidates.length} rows={candidates} plainCount={`${candidates.length} shown`} />
          <Table columns={withMatchColumn(columns, search)} data={candidates} isLoading={candidatesQuery.isLoading} emptyMessage="No candidates match this filter." />
        </>
      )}

      <CandidateTimelineModal candidate={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
